// Компоненты лицензионных предупреждений и блокировки бекофиса:
//  • LicenseBanner — мягкое предупреждение (янтарное) за 6–10 дней, скрывается на день;
//  • LicenseDailyModal — модалка-напоминание за 0–5 дней, всплывает при каждом открытии;
//  • LicenseLockout — полноэкранный экран блокировки (заменяет весь каркас приложения).
//
// Данные берутся из useLicense(); супер-админ никогда не блокируется и предупреждений
// не видит (провайдер для него пассивен).

import { useEffect, useState } from 'react'
import { useAuth } from '@/auth/AuthContext'
import { useLicense } from '@/context/LicenseContext'
import { Button, Modal } from '@/components/ui'
import { dateOnly } from '@/lib/format'
import { api } from '@/api/client'
import { toMessage } from '@/hooks/useApi'

/** Контакты поддержки по умолчанию, если в настройках клуба пусто. */
const DEFAULT_CONTACTS = 'Telegram @VyacheslavZloy'

/** Ключ localStorage для «скрыто на сегодня» (баннер). */
function bannerKeyForToday(): string {
  const d = new Date()
  const p = (n: number): string => String(n).padStart(2, '0')
  const day = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
  return `bo.license.banner.${day}`
}

// ─────────────────────────── Баннер (6–10 дней) ───────────────────────────

export function LicenseBanner(): React.JSX.Element | null {
  const { blocked, daysLeft, expiresAt } = useLicense()
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(bannerKeyForToday()) === '1'
    } catch {
      return false
    }
  })

  if (blocked) return null
  if (daysLeft == null || expiresAt == null) return null
  if (daysLeft < 6 || daysLeft > 10) return null
  if (dismissed) return null

  const onDismiss = (): void => {
    try {
      localStorage.setItem(bannerKeyForToday(), '1')
    } catch {
      /* localStorage недоступен — просто скрываем на сессию */
    }
    setDismissed(true)
  }

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 18px',
        background: 'var(--gold-wash)',
        borderBottom: '1px solid var(--amber)',
        color: 'var(--text)',
        fontSize: 14
      }}
    >
      <span style={{ color: 'var(--amber)', fontSize: 18, lineHeight: 1 }}>⚠</span>
      <span style={{ flex: 1 }}>
        Срок лицензии истекает <strong>{dateOnly(expiresAt)}</strong> — осталось {daysLeft} дн.
        Продлите, иначе доступ будет заблокирован.
      </span>
      <Button variant="ghost" size="sm" type="button" onClick={onDismiss} title="Скрыть до завтра">
        Понятно
      </Button>
    </div>
  )
}

// ─────────────────────────── Модалка-напоминание (0–5 дней) ───────────────────────────

export function LicenseDailyModal(): React.JSX.Element | null {
  const { blocked, daysLeft, expiresAt, contacts } = useLicense()
  // Закрывается только на сессию — на следующем открытии бекофиса всплывёт снова.
  const [closed, setClosed] = useState(false)

  if (blocked) return null
  if (daysLeft == null) return null
  if (daysLeft < 0 || daysLeft > 5) return null
  if (closed) return null

  const support = contacts.trim() || DEFAULT_CONTACTS
  const expiryText = expiresAt != null ? dateOnly(expiresAt) : '—'
  const title = daysLeft === 0 ? 'Лицензия истекает сегодня' : 'Срок лицензии заканчивается'

  return (
    <Modal
      title={title}
      onClose={() => setClosed(true)}
      footer={
        <Button variant="primary" type="button" onClick={() => setClosed(true)}>
          Понятно
        </Button>
      }
    >
      <p className="modal__text" style={{ marginTop: 0 }}>
        {daysLeft === 0 ? (
          <>
            Срок действия лицензии истекает <strong>сегодня</strong> ({expiryText}). После окончания
            доступ к бекофису и терминалам будет заблокирован.
          </>
        ) : (
          <>
            До окончания срока действия лицензии осталось <strong>{daysLeft} дн.</strong> (до{' '}
            {expiryText}). После окончания доступ будет заблокирован.
          </>
        )}
      </p>
      <p className="modal__text">
        Пожалуйста, продлите подписку заранее — картой онлайн или банковским переводом по счёту из
        раздела «Финансы».
      </p>
      <SubscriptionPayButton />
      <p className="modal__text">Вопросы по оплате — поддержка:</p>
      <p className="modal__text" style={{ fontWeight: 600 }}>
        {support}
      </p>
    </Modal>
  )
}

// ─────────────────────────── Оплата подписки картой ───────────────────────────

/**
 * Кнопка «Оплатить подписку картой» для экрана блокировки: неоплаченный счёт
 * бэкенд находит сам (или выставляет на месяц по ставке клуба) и отдаёт ссылку на
 * платёжную страницу bePaid. Эндпоинты помечены @AllowExpired, поэтому работают и
 * при истёкшей подписке — иначе платить было бы нечем.
 *
 * Ничего не рисуем, если эквайринг не подключён (BEPAID_* не заданы) или у
 * сотрудника нет права платить (viewer).
 */
function SubscriptionPayButton(): React.JSX.Element | null {
  const { hasRole } = useAuth()
  const [enabled, setEnabled] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    api
      .get<{ enabled: boolean }>('/v1/payments/config')
      .then((r) => {
        if (active) setEnabled(r.enabled === true)
      })
      .catch(() => {
        // Эквайринг недоступен — остаёмся с контактами поддержки.
      })
    return () => {
      active = false
    }
  }, [])

  if (!enabled || !hasRole('owner', 'admin', 'accountant')) return null

  const pay = async (): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      const res = await api.post<{ redirectUrl: string }>('/v1/finance/subscription/checkout')
      window.location.assign(res.redirectUrl)
    } catch (err) {
      setError(toMessage(err))
      setBusy(false)
    }
  }

  return (
    <div style={{ margin: '0 0 20px' }}>
      <Button variant="primary" type="button" disabled={busy} onClick={() => void pay()}>
        {busy ? 'Открываем оплату…' : '💳 Оплатить подписку картой'}
      </Button>
      {error && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 8 }}>{error}</div>}
    </div>
  )
}

// ─────────────────────────── Экран блокировки ───────────────────────────

export function LicenseLockout(): React.JSX.Element {
  const { logout } = useAuth()
  const { expiresAt, contacts } = useLicense()
  const support = contacts.trim() || DEFAULT_CONTACTS

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--bg)'
      }}
    >
      <div
        role="alert"
        className="card"
        style={{
          maxWidth: 520,
          width: '100%',
          padding: 32,
          textAlign: 'center',
          borderColor: 'var(--red-glow)'
        }}
      >
        <div style={{ fontSize: 48, lineHeight: 1, marginBottom: 12 }}>🔒</div>
        <h2 style={{ margin: '0 0 8px', color: 'var(--red)' }}>Доступ заблокирован</h2>
        <p style={{ color: 'var(--text-2)', margin: '0 0 20px' }}>
          {expiresAt != null ? (
            <>Срок действия лицензии истёк ({dateOnly(expiresAt)}).</>
          ) : (
            <>Срок действия лицензии истёк.</>
          )}
        </p>

        <div
          style={{
            background: 'var(--surface-2)',
            borderRadius: 10,
            padding: '14px 16px',
            margin: '0 0 20px',
            textAlign: 'left'
          }}
        >
          <div
            style={{
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--text-3)',
              marginBottom: 6
            }}
          >
            Контакты поддержки
          </div>
          <div style={{ fontWeight: 600, color: 'var(--text)' }}>{support}</div>
        </div>

        <SubscriptionPayButton />

        <p style={{ color: 'var(--text-2)', margin: '0 0 24px', fontSize: 14 }}>
          Оплата картой снимет блокировку сразу после подтверждения платежа. Также можно оплатить
          счёт банковским переводом или связаться с поддержкой.
        </p>

        <Button variant="ghost" type="button" onClick={logout}>
          Выйти
        </Button>
      </div>
    </div>
  )
}
